/**
 * HTML-Inhalt für das Planeten-Detailpanel.
 * Ressourcen-Balken, Energie-Balken, Konstanten.
 */

import type { PlanetOwnership } from '../world/PlayerWorldTypes';
import { formatNumber, formatFull } from '../math/formatNumber';

export const PLANET_NAMES: Record<string, string> = {
  terran: 'Terran', desert: 'Desert', ice: 'Ice', lava: 'Lava',
  gas_giant: 'Gas Giant', ocean: 'Ocean', volcanic: 'Volcanic',
};
export const PLANET_ICONS: Record<string, string> = {
  terran: '\u{1F30D}', desert: '\u{1F3DC}\uFE0F', ice: '\u{2744}\uFE0F',
  lava: '\u{1F30B}', gas_giant: '\u{1FA90}', ocean: '\u{1F30A}', volcanic: '\u{1F30B}',
};
export const OWNER_COLOR: Record<PlanetOwnership, string> = {
  own: '#2a6', unclaimed: '#999', other: '#c44',
};

export const MEEPLE_SVG = (color: string) =>
  `<svg width="20" height="20" viewBox="0 0 24 24" fill="${color}">` +
  `<path d="M12 2a4 4 0 0 1 4 4c0 1.5-.8 2.8-2 3.5L16 12l3 8H5l3-8 2-2.5A4 4 0 0 1 12 2z"/>` +
  `</svg>`;

export function ownerLabel(o: PlanetOwnership): string {
  if (o === 'own') return 'Dein Planet';
  if (o === 'unclaimed') return 'Nicht beansprucht';
  return 'Fremder Spieler';
}

const UNKNOWN_BAR =
  `<div style="height:4px;background:rgba(255,255,255,0.06);border-radius:2px;position:relative">` +
  `<span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
  `font-size:11px;color:rgba(255,255,255,0.25)">?</span></div>`;

export function energyBar(solarEnergy: number | null, known: boolean): string {
  const maxBar = 200;
  if (!known) {
    return (
      `<div style="margin-bottom:8px">` +
      `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
      `<span style="color:#aaa">\u2600\uFE0F Solarenergie</span>` +
      `<span style="color:#556">??</span></div>${UNKNOWN_BAR}</div>`
    );
  }
  const val = solarEnergy ?? 0;
  const pct = Math.min(100, Math.round((val / maxBar) * 100));
  return (
    `<div style="margin-bottom:8px">` +
    `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
    `<span style="color:#aaa">\u2600\uFE0F Solarenergie</span>` +
    `<span style="pointer-events:auto;color:#fd0">${val.toFixed(1)}</span></div>` +
    `<div style="height:4px;background:rgba(255,255,255,0.08);border-radius:2px">` +
    `<div style="height:100%;width:${pct}%;background:#fd0;border-radius:2px"></div></div></div>`
  );
}

export function resourceBar(
  label: string, mined: number, deposit: number, color: string, known: boolean,
): string {
  if (!known) {
    return (
      `<div style="margin-bottom:6px">` +
      `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
      `<span style="color:#aaa">${label}</span>` +
      `<span style="color:#556">?? / ??</span></div>${UNKNOWN_BAR}</div>`
    );
  }
  const remaining = deposit - mined;
  const pct = deposit > 0 ? Math.min(100, Math.round((remaining / deposit) * 100)) : 0;
  const tip = `Verbleibend: ${formatFull(remaining, 't')}\nVorkommen: ${formatFull(deposit, 't')}`;
  return (
    `<div style="margin-bottom:6px">` +
    `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
    `<span style="color:#aaa">${label}</span>` +
    `<span style="pointer-events:auto;cursor:default" data-tip="${tip}">` +
    `<span style="color:#556">${formatNumber(remaining, 't')}</span>` +
    `<span style="color:${color}"> / ${formatNumber(deposit, 't')}</span></span></div>` +
    `<div style="height:4px;background:rgba(255,255,255,0.08);border-radius:2px">` +
    `<div style="height:100%;width:${pct}%;background:${color};border-radius:2px"></div></div></div>`
  );
}
